import { useFormContext, useWatch } from 'react-hook-form'
import { useCallback, useMemo, useContext } from 'react'
import Button from '../../Button'
import Auftrag1_10ZeileForm from './Auftrag1_10ZeileForm'
import { BefehlFormAuftrag } from '../Generic'
import { useTranslation } from 'react-i18next'
import { Auftrag1_10ZeileData, DEFAULT_AUFTRAG1_10_ZEILE } from './schema'
import { AddCircleIcon24 } from '../../../icons'
import MarkAuftraegeAsDoneContext from '../../../contexts/MarkAuftraegeAsDoneContext'

export interface Auftrag1_10FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
}

/**
 * Form component for Auftrag 1.10
 */
export default function Auftrag1_10Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag1_10FormProps>) {
  const { t } = useTranslation('ui-befehle')

  const formContext = useFormContext()
  const { setValue, getValues, control } = formContext

  const formErrors = useMemo(() => {
    return formContext.formState.errors
  }, [formContext.formState.errors])

  const zeilen = useWatch({
    control,
    name: 'auftrag_1_10.zeilen'
  })

  const zeileCount = zeilen?.length ?? 0

  const context = useContext(MarkAuftraegeAsDoneContext)
  const displaySlideToUnlock =
    (context?.isBefehlsnachrichtQuittiert && zeileCount >= 2) || false

  const addZeile = useCallback(() => {
    if (zeileCount >= 3) {
      return
    }

    const currentZeilen = getValues('auftrag_1_10.zeilen') ?? []
    setValue(
      'auftrag_1_10.zeilen',
      [...currentZeilen, { ...DEFAULT_AUFTRAG1_10_ZEILE }],
      {
        shouldValidate: false,
        shouldDirty: true
      }
    )
  }, [setValue, getValues, zeileCount])

  const removeLastZeile = useCallback(() => {
    const currentZeilen = getValues('auftrag_1_10.zeilen') ?? []
    if (currentZeilen.length <= 1) return

    const newZeilen = currentZeilen.slice(0, -1)
    setValue('auftrag_1_10.zeilen', newZeilen, {
      shouldValidate: false,
      shouldDirty: true
    })
  }, [setValue, getValues])

  const zeileComponents = useMemo(() => {
    const zeilenToRender =
      zeilen && Array.isArray(zeilen) && zeilen.length > 0
        ? zeilen
        : [{ ...DEFAULT_AUFTRAG1_10_ZEILE }]

    return zeilenToRender.map((_zeile: Auftrag1_10ZeileData, index: number) => (
      <Auftrag1_10ZeileForm
        key={`zeile-${index}`}
        zeile={index + 1}
        displaySlideToUnlock={displaySlideToUnlock}
        auftragKennung="1.10"
        fieldPrefix={`auftrag_1_10.zeilen.${index}`}
        readOnly={readOnly}
        testId={`${testId}-zeile-${index}`}
        showRemoveButton={
          !readOnly && index > 0 && index === zeilenToRender.length - 1
        }
        onRemove={() => removeLastZeile()}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
        errors={formErrors}
      />
    ))
  }, [
    zeilen,
    readOnly,
    testId,
    displaySlideToUnlock,
    activateEmptyFieldWarnings,
    formErrors,
    removeLastZeile
  ])

  return (
    <BefehlFormAuftrag
      auftragKennung="1.10"
      fieldName="auftrag_1_10.enabled"
      readOnly={true}
      testId={testId}
    >
      <span data-testid={`${testId}-text`}>{t('auftraege.1_10.label')}</span>
      <div className="w-full">
        {/* Line inputs */}
        <div className="flex flex-col gap-1">{zeileComponents}</div>
        {/* Control buttons */}
        {!readOnly && zeileCount < 3 && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="tertiary-plain"
              onClick={addZeile}
              className="flex items-center text-blue-600 min-h-[44px] min-w-[44px] px-1 py-0.5"
              data-testid={`${testId}-add-zeile-button`}
            >
              <AddCircleIcon24
                data-testid={`${testId}-add-zeile-button-icon`}
              />
              <span
                className="ml-0.5 text-sm"
                data-testid={`${testId}-add-zeile-button-text`}
              >
                {t('auftraege.1_10.zeile.add')}
              </span>
            </Button>
          </div>
        )}
      </div>
    </BefehlFormAuftrag>
  )
}
